import { defaultLegacyPlans } from '@constants/plans/legacySpacePlans'
import { USER_ROLES } from '@constants/user'
import type { RegionServerKeys } from '@/constants/region'
import type {
  Locale,
  Org,
  User,
  Collaborator,
  BillingAddress,
  SpaceRole,
  AssetMetadataSchema,
  DefaultMetadataKey,
} from './index'

export type FeatureLimitMessage = {
  default: string
  highestPLG: string
  enterprise?: string
}

export type FeatureLimitExceededFlags = {
  roles?: boolean
  assets?: boolean
  folders?: boolean
  datasources?: boolean
  stories?: boolean
  preview_urls?: boolean
  api_requests?: boolean
}

export type Limit = {
  max_collaborators?: number
  min_character_search?: number
  plan_level?: number
  saved_webhook?: boolean
} & SpaceOption

export type AssetSignature = object
export type CrawlConfig = object

export enum FeVersion {
  V1 = 'v1',
  V2 = 'v2',
}

export type LimitedSpace = {
  user_count?: number
  is_dev?: boolean
  blocked_at?: null | Date
  canceled_at?: null | Date
  unpaid_at?: null | Date
  stripe_trial_end?: null | Date
  development_mode: boolean
  name: string
  id: number
  euid?: null | string
  region?: string
  owner_id?: number
  updated_at: Date
  stripe_trial_status?: null | StripeTrialStatuses
  subscription_status?: 'active' | 'canceled' | 'unpaid' | 'blocked'
  plan: string
  plan_level: number
  trial?: boolean
  requires_2fa: boolean | null
  org_requires_2fa?: boolean | null
  org_status?: string
  created_at: Date
  partner_id?: number | null
  org_id?: number | null
  space_type?: SpaceType
  pending_limit_boosts?: null | boolean
  pending_traffic_boosts?: null | boolean
  collaborators?: Collaborator[]
  space_roles?: SpaceRole[]
}

export enum SubscriptionBillingCycle {
  Month = 'month',
  Year = 'year',
}

export enum StripeTrialStatuses {
  PENDING = 'pending_subscription',
  SUBSCRIBED = 'subscribed',
}

export type SpaceOption = {
  languages: Locale[]
  mobile_size: number
  tablet_size: number
  hosted_backup: boolean
  onboarding_step: string
  visual_mode_disable?: unknown
  required_assest_fields: string[]
  translatable_asset_fields: DefaultMetadataKey[]
  use_translated_stories: boolean
  default_lang_name?: string
  space_subscription_interval: SubscriptionBillingCycle | null
  duplicated_from: string
  canceled_at?: string
  cloudinary_api_key?: string
  cloudinary_cloud_name?: string
  encode_preview_urls?: boolean
  exceeded_additional_usage?: boolean
  has_breach_of_maximum_limits?: boolean
}

export type FeatureLimitTerm = {
  key: string
  name: string
  version: string
  url: string | null
  id: number
}

export type FeatureLimit = {
  key: string
  origin: string
  limit: string
  limit_type: string
  is_available: boolean
  terms: FeatureLimitTerm[]
}

export type SpaceEnvironment = {
  from_blueprint?: string
  name: string
  location: string
}

export type DomainRoute = {
  pattern: string
  template: string
}

export type AllowedPlanName =
  | (typeof defaultLegacyPlans)[number]
  | 'trial'
  | 'devmode'
  | 'starter'
  | 'teams'
  | 'enterprise'
  | 'enterprise_plus'
  | 'enterprise_essentials'
  | 'enterprise_scale'
  | 'enterprise_ultimate'
  | 'starter_2025' // the "_2025" suffix is used to differentiate with the "starter" legacy plan
  | 'growth'
  | 'growth_plus'
  | 'premium_2025' // the "_2025" suffix is used to differentiate with the "premium" legacy plan
  | 'elite'
  | 'development_mode' // "Development Mode now has a dedicated plan"

export type Space = {
  [key: string]: unknown
  ai_text_generator_feature_disabled: boolean
  ai_text_generator_disabled: boolean // if feature is enable per space from the Storyblok lab features
  ai_text_generator_disabled_in_org: boolean // if feature is enable per org from the Storyblok lab features
  allow_space_ai_styles?: boolean // Only spaces that belong to an organization
  inherit_org_ai_configuration: boolean
  api_requests: number
  asset_aws_role?: unknown
  asset_cdn?: unknown
  asset_custom_meta_data_schema: AssetMetadataSchema[]
  asset_region?: unknown
  asset_signature: AssetSignature
  assets_count: number
  assistance_mode: boolean
  backup_frequency?: 'daily' | 'weekly' | 0
  backup_time?: string
  backup_week_day?: string
  blocked_at: string
  // The reference is set when the blueprint setup is in-progress.
  blueprint_reference?: string | null
  branch_deployed_hook: string | null | undefined
  billing_address: BillingAddress
  canceled_at?: string
  cloudinary_api_key?: unknown
  cloudinary_cloud_name?: unknown
  collaborators: Collaborator[]
  crawl_config: CrawlConfig
  created_at: string
  customer_id: string
  custom_s3_asset_bucket?: unknown
  custom_upload_limit_in_mb?: number
  datasource_entry_saved_hook: string | null | undefined
  default_lang_name?: string
  default_root: string
  dimensions_app_folder_ids?: number[]
  dimensions_app_folders?: {
    folder_id: number
    ai_translation_code: string
  }[]
  enable_dimensions_app_ai_translation?: boolean
  domain: string
  duplicatable?: unknown
  duplicated_from: string
  encode_preview_urls?: boolean
  environments?: SpaceEnvironment[]
  euid?: unknown
  exceeded_requests: number
  feature_limit_exceeded_flags: FeatureLimitExceededFlags
  feature_limits: FeatureLimit[]
  first_token: string
  has_pending_tasks: boolean
  has_slack_webhook: boolean
  show_stories_alternative_versions: boolean
  hosted_backup: boolean
  id: number
  is_demo?: boolean
  isFavorite: boolean
  label: string
  languages: Locale[]
  limits: Limit
  maintenance?: boolean
  mobile_size: number
  monthly_ai_tokens_traffic: number
  monthly_ai_tokens_traffic_limit: number
  monthly_ai_credits_traffic?: number
  name: AllowedPlanName
  no_cc_community: boolean
  onboarding_step: string
  options: SpaceOption
  org:
    | (Org & {
        ai_text_generator_disabled_in_org: boolean
      })
    | null
  org_id: number | null
  owner: User
  owner_id: number
  parent_id?: number
  partner_id: number | null
  personal_s3_asset_bucket?: unknown
  plan: string
  plan_level: number
  private_assets?: boolean
  private_assets_token: string | null | undefined
  published_is_links_default?: unknown
  region: RegionServerKeys
  release_merged_hook: string | null | undefined
  repository_url?: string
  required_assest_fields: DefaultMetadataKey[]
  rev_share_enabled?: unknown
  role: keyof typeof USER_ROLES
  routes: DomainRoute[]
  searchblok_id?: unknown
  settings: unknown[]
  space_roles: SpaceRole[]
  space_subscription_interval?:
    | SubscriptionBillingCycle.Month
    | SubscriptionBillingCycle.Year
    | null
  space_type?: SpaceType
  stage_changed_hook: string | null | undefined
  story_published_hook: string | null | undefined
  story_saved_hook: string | null | undefined
  stories_count: number
  stripe_trial_end: Date | null
  stripe_trial_status: StripeTrialStatuses | null | undefined
  strong_auth: boolean
  subscription_status: 'active' | 'canceled' | 'unpaid' | 'blocked'
  tablet_size: number
  track_statistics?: boolean
  translatable_asset_fields: DefaultMetadataKey[]
  traffic_limit: number
  trial: boolean
  trial_expires_at: string | null | undefined
  uniq_domain?: unknown
  uniq_subdomain: string
  unpaid_at?: string
  updated_at: string
  use_translated_stories: boolean
  user_count: number
  verified: boolean
  visual_mode_disable: boolean | null | undefined
  webhook_token: string | null | undefined
  enable_extended_char_set_in_stories_slug?: boolean | null
  disclaimer_ids: number[]
  auto_delete_old_versions_after_days: number | null
}

// TODO: remove this type and LimitedSpace should be used instead
export type PartnerSpace = {
  id: number
  name: string
  user_count: number
  created_at: null | Date
  updated_at: null | Date
  plan: string
  plan_level: number
  development_mode: boolean
  requires_2fa: boolean | null
  is_dev: boolean
  org_id: number
}

export type CreateSpace = {
  server?: string
  space: {
    name: string
    is_demo?: boolean | string
    blueprint_reference?: string
  }
  in_org?: boolean
  dup_id?: string
  space_type?: SpaceType
}

export type StandardSpaceType = 1
export type ProSpaceType = 2
export type SpaceType = StandardSpaceType | ProSpaceType

export type SpaceTypeCount = {
  spaceStandardCount: number
  spaceProCount: number
}

export type SpaceTrafficBoost = {
  id: number
  invoice_url: string
  status: 'open' | 'paid' | 'unpaid'
  value: string
  created_at: Date
}

export type SpaceAdditionalAICredit = {
  id: number
  invoice_url: string
  status: 'open' | 'paid' | 'unpaid'
  value: string
  created_at: Date
}

export type LastBoostInvoiceInfo = {
  currency: string
  packs_used: number
  plan_level?: number
  pack_amount: number
  price_value: number
  total_count?: number
  over_the_limit_count?: number
  feature?: string
  total_price?: number
}

export type LastBoostInvoice = {
  id: number
  status: 'open' | 'paid' | 'unpaid'
  info: {
    assets: LastBoostInvoiceInfo
    stories: LastBoostInvoiceInfo
    datasources: LastBoostInvoiceInfo
    custom_roles: LastBoostInvoiceInfo
    content_folders: LastBoostInvoiceInfo
    api_requests: LastBoostInvoiceInfo
    preview_urls: LastBoostInvoiceInfo
    hosted_invoice_url?: string
    period?: string
    breach_maximum_limit_addon?: LastBoostInvoiceInfo
  }

  created_at: Date
}
